import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Redirect } from "react-router-dom";

import { GET_STAND } from "graphql/query/stand-gql";

import Stand from "components/stand";
import Exhibition from "components/exhibition";
import { useMe, useVarParam } from "components/use-hooks";
import { Wraps } from "components/layout";
import Localize, { localizeIt } from "components/service/localize";
import { Loader, NoData } from "components/request-result";
import checkUser from "utils/check-user";

const { getUserPermissions } = checkUser;

const { Helpers: { EE } } = Exhibition;

const StandPage = ({ standId }) => {
    const { data, loading } = useQuery(GET_STAND, {
        variables: {
            id: standId
        },
        fetchPolicy: 'cache-and-network',
    });

    const appParams = useVarParam(),
        { me } = useMe(),
        { isEventPermitted, hasActiveEvent } = checkUser;
    const { hasViewNotRelatedPermission } = getUserPermissions(me);

    const {
        stand = {}
    } = data ? data : {};

    const hasEventForThisStand = isEventPermitted(me, stand?.ee_id);
    const viewingWithSpecialPermission = hasViewNotRelatedPermission && !hasEventForThisStand;
    const shouldShowBottomBar = !viewingWithSpecialPermission;
    const standPermitted = hasEventForThisStand || hasViewNotRelatedPermission;
    const hasUserActiveEvent = hasActiveEvent(me);

    const exhibitionTitle = stand?.exhibitionEvent?.title || "";
    const enhancedExhibitionTitle = viewingWithSpecialPermission
        ? `${ exhibitionTitle } <br> (${ localizeIt("STAND.External_Exhibition") })`
        : localizeIt("PAGE.Title_Stand");

    useEffect(() => {
        appParams.set({
            headerTitle: viewingWithSpecialPermission ? enhancedExhibitionTitle : localizeIt("PAGE.Title_Stand"),
            bottomBar: shouldShowBottomBar,
            viewingWithoutEvent: viewingWithSpecialPermission
        });

        return () => {
            appParams.set({
                headerTitle: null,
                bottomBar: false,
                viewingWithoutEvent: false
            });
        };
    }, [ appParams, viewingWithSpecialPermission, enhancedExhibitionTitle, shouldShowBottomBar ]);

    if (!loading && (!standPermitted || !stand || !data) && !hasUserActiveEvent)
    {
        return <Redirect to="/exhibitions" />;
    }

    return (
        <Wraps.Container className="page stand-page">
            { data && stand && standPermitted &&
                <>
                    <h3>{ stand.exhibitor }</h3>
                    <Stand.Blocks.Overview
                        { ...stand }
                    />
                    <Stand.Blocks.Materials
                        { ...stand }
                    />
                    <Stand.Blocks.KeyLogs
                        standId={ stand.id }
                    />
                    { EE.damageOldOrNew(appParams) === "new" &&
                        <Stand.Blocks.Damages
                            stand={ stand }
                            damageReport={ !!stand.damageReport ? stand.damageReport : {} }
                        />
                    }
                    <Stand.Blocks.Crafts
                        crafts={ stand.crafts }
                        stand={ stand }
                    />
                </>
            }
            { !loading && (!standPermitted || !stand || !data) && hasUserActiveEvent &&
                <NoData
                    text = { {
                        before : <Localize>NO_DATA.Stand_DoesNotExist</Localize>
                    } }
                    icon = "stand"
                    className = "block"
                />
            }
            { loading && <Loader type="block" /> }
        </Wraps.Container>
    );
}

export default StandPage;